import React from 'react';
import { AnimatePresence, motion, useMotionValueEvent, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';

export function PrivacySection() {
  const { t } = useLanguage();
  const sectionRef = React.useRef<HTMLElement>(null);

  const titlePrefix = `${t('hero.privacyTitle')} `;
  const secureWord = t('hero.privacyBlur');
  const titleSuffix = t('hero.privacySuffix');
  const fullSubtitle = t('hero.privacyDesc');

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  const [isPinned, setIsPinned] = React.useState(false);
  const [scrambledWord, setScrambledWord] = React.useState(secureWord);
  const scrambleSeedRef = React.useRef<number>(-1);

  const scrambleProgress = useTransform(scrollYProgress, [0.12, 0.92], [0, 1], { clamp: true });

  const shackleRotate = useTransform(scrollYProgress, [0.14, 0.42, 0.7, 0.92], [-55, -18, 6, 0]);
  const shackleY = useTransform(scrollYProgress, [0.14, 0.42, 0.7, 0.92], [-5, -1, 0, 0]);
  const bodyShadow = useTransform(
    scrollYProgress,
    [0.18, 0.46, 0.84],
    [
      'drop-shadow(0 0 0 rgba(0,0,0,0))',
      'drop-shadow(0 18px 32px color-mix(in oklab, var(--nt-primary) 35%, transparent))',
      'drop-shadow(0 0 0 rgba(0,0,0,0))',
    ],
  );

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    setIsPinned(v > 0.12 && v < 0.88);
  });

  useMotionValueEvent(scrambleProgress, 'change', (v) => {
    const intensity = v < 0.5 ? 1 : Math.max(0, 1 - (v - 0.5) / 0.5);
    if (intensity <= 0.001) {
      scrambleSeedRef.current = -1;
      setScrambledWord(secureWord);
      return;
    }

    const symbols = '#@$%*&';
    const seedBase = Math.floor(v * 70);
    if (seedBase === scrambleSeedRef.current) return;
    scrambleSeedRef.current = seedBase;

    function prng(n: number) {
      const x = Math.sin(n) * 10000;
      return x - Math.floor(x);
    }

    const next = secureWord
      .split('')
      .map((ch, i) => {
        if (ch === ' ') return ch;
        const r = prng(seedBase * 31 + i * 97 + 13);
        if (r < intensity) {
          const pick = Math.floor(prng(seedBase * 131 + i * 19 + 7) * symbols.length);
          return symbols[pick] ?? '#';
        }
        return ch;
      })
      .join('');

    setScrambledWord(next);
  });

  const renderedSecureWord = React.useMemo(() => {
    const chars = secureWord.split('');
    return chars
      .map((ch, i) => {
        if (ch === ' ') return ch;
        return scrambledWord[i] ?? ch;
      })
      .join('');
  }, [scrambledWord, secureWord]);

  return (
    <section ref={sectionRef} data-scroll-section className="relative theme-bg min-h-[320vh] py-40 overflow-hidden">
      <AnimatePresence>
        {isPinned && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-0 z-30 pointer-events-none flex items-center justify-center"
          >
            <div className="container mx-auto px-6 relative z-10 flex flex-col items-center text-center">
              <div className="mb-8 relative w-24 h-24 flex items-center justify-center">
                <svg width="80" height="100" viewBox="0 0 80 100" className="overflow-visible">
                  <motion.path
                    d="M20 35 V20 A20 20 0 0 1 60 20 V35"
                    fill="none"
                    stroke="var(--nt-primary)"
                    strokeWidth="10"
                    strokeLinecap="round"
                    style={{
                      translateY: shackleY,
                      rotate: shackleRotate,
                      originX: "20px",
                      originY: "35px",
                    }}
                  />
                  <motion.rect
                    x="10"
                    y="35"
                    width="60"
                    height="50"
                    rx="8"
                    fill="var(--nt-primary)"
                    style={{ filter: bodyShadow }}
                  />
                  <circle cx="40" cy="55" r="4" fill="var(--background)" />
                  <rect x="38" y="55" width="4" height="10" rx="2" fill="var(--background)" />
                </svg>
              </div>

              <h2 className="relative text-4xl md:text-5xl lg:text-6xl font-light mb-8 max-w-4xl tracking-tight text-[color:var(--foreground)] whitespace-nowrap">
                <span>{titlePrefix}</span> <br />
                <span className="font-mono">{renderedSecureWord}</span>
                <span>{titleSuffix}</span>
              </h2>

              <p className="text-lg max-w-2xl leading-relaxed text-[color:var(--foreground-muted)]">
                {fullSubtitle}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
